// Supabase fetchers for briefing flights, shared by the producer editor
// (web), the read-only portal view (web) and the crew app (mobile), in
// the same spirit as briefingQueries.ts: one definition of the query so
// the three sides can't drift on shape, ordering or filtering.
//
// Access is enforced by RLS, not here (migration 20260803120000):
//   * producers / co-producers read every leg + passenger of their own
//     briefing;
//   * a crew member reads only legs they are a passenger on, and only
//     their OWN passenger row — never a colleague's booking reference.
// These fetchers therefore issue the same SQL for both roles; the
// database decides what comes back.

import {
  parseFlightLegs,
  parseFlightPassengers,
  type FlightLeg,
  type FlightPassenger,
} from "./flights";

// deno-lint-ignore no-explicit-any
type Client = any; // kept loose so both web and mobile Supabase clients work

export interface BriefingFlightData {
  legs: FlightLeg[];
  passengers: FlightPassenger[];
}

const EMPTY: BriefingFlightData = { legs: [], passengers: [] };

const LEG_COLUMNS =
  "id, briefing_id, direction, leg_order, airline, flight_number, " +
  "departure_airport, departure_airport_name, departure_terminal, departure_check_in_desk, departure_at, " +
  "arrival_airport, arrival_airport_name, arrival_terminal, arrival_at, " +
  "departure_utc_offset_minutes, arrival_utc_offset_minutes, " +
  "duration_minutes, aircraft, notes";

const PASSENGER_COLUMNS =
  "id, flight_id, briefing_crew_id, booking_reference, seat, baggage, special_baggage";

/**
 * Every flight leg on a briefing plus the passenger rows the caller may
 * see. Both queries throw on error rather than degrading to [] — a
 * transient RLS/auth hiccup that returned an empty list would make the
 * crew app render "no flights booked" for someone who does have a
 * flight, which is worse than showing the last cached answer with an
 * error state (same reasoning as fetchCrewAssignments).
 */
export async function fetchBriefingFlights(
  client: Client,
  briefingId: string,
): Promise<BriefingFlightData> {
  if (!briefingId) return EMPTY;

  const { data: legRows, error: legError } = await client
    .from("briefing_flights")
    .select(LEG_COLUMNS)
    .eq("briefing_id", briefingId)
    .order("direction")
    .order("leg_order");
  if (legError) throw legError;

  const legs = parseFlightLegs(legRows);
  if (legs.length === 0) return EMPTY;

  const { data: passengerRows, error: passengerError } = await client
    .from("briefing_flight_passengers")
    .select(PASSENGER_COLUMNS)
    .in(
      "flight_id",
      legs.map((leg) => leg.id),
    );
  if (passengerError) throw passengerError;

  return { legs, passengers: parseFlightPassengers(passengerRows) };
}

/**
 * The `briefing_crew` id for one user on one briefing — the stable
 * per-person key flights hang off.
 *
 * Read from crew_members rather than briefing_crew directly: a crew
 * member is guaranteed to have day-rows there with a resolved user_id,
 * while briefing_crew.user_id is backfilled asynchronously from email
 * and may still be null right after the producer adds them. Returns null
 * when the user is not on this briefing.
 */
export async function fetchOwnBriefingCrewId(
  client: Client,
  briefingId: string,
  userId: string,
): Promise<string | null> {
  if (!briefingId || !userId) return null;
  const { data, error } = await client
    .from("crew_members")
    .select("briefing_crew_id")
    .eq("briefing_id", briefingId)
    .eq("user_id", userId)
    .not("briefing_crew_id", "is", null)
    .limit(1);
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : null;
  return row?.briefing_crew_id ?? null;
}

/**
 * The crew of a briefing, by name — the list the flights UI ticks people
 * off from. Here rather than inline in each component so the producer
 * editor, the dialog, the review page and the PDF list the same people
 * in the same order.
 */
export async function fetchBriefingCrewNames(
  client: Client,
  briefingId: string,
): Promise<Array<{ id: string; name: string }>> {
  if (!briefingId) return [];
  const { data, error } = await client
    .from("briefing_crew")
    .select("id, name")
    .eq("briefing_id", briefingId)
    .order("name");
  if (error) throw error;
  return data ?? [];
}

export interface OwnFlightData extends BriefingFlightData {
  /** The reader's own briefing_crew id, or null when not on this briefing. */
  briefingCrewId: string | null;
}

/**
 * The crew app's view: the reader's own briefing_crew id plus whatever
 * RLS lets them read. Bundled into one fetcher so the mobile briefing
 * tab needs a single query key and the production-selector prefetch can
 * warm the exact same entry.
 */
export async function fetchOwnFlights(
  client: Client,
  briefingId: string,
  userId: string,
): Promise<OwnFlightData> {
  // In parallel: the two reads share no data. Chaining them cost the
  // crew app's briefing tab an extra round trip on every open — the one
  // screen most likely to be loaded on airport wifi. Someone not on the
  // briefing pays one wasted (RLS-empty) query instead.
  const [briefingCrewId, data] = await Promise.all([
    fetchOwnBriefingCrewId(client, briefingId, userId),
    fetchBriefingFlights(client, briefingId),
  ]);
  if (!briefingCrewId) return { ...EMPTY, briefingCrewId: null };
  return { ...data, briefingCrewId };
}
